import { For } from '../c-mp/comp/For'
import { Icon } from '../c-mp/comp/Icon'
import { $when, Show } from '../c-mp/comp/Show'
import { Slot } from '../c-mp/comp/Slot'
import { defineComponent } from '../c-mp/fun/defineComponent'
import { useQuery } from '../c-mp/fun/useQuery'
import { mutateState } from '../c-mp/fun/useState'
import { expectDefined } from '../fun/expectDefined'
import { fetchPersonIdsWithImages } from '../fun/fetchPersonIdsWithImages'
import { fetchPersonImagesList } from '../fun/fetchPersonImagesList'
import { formatNumber } from '../fun/formatNumber'
import { getDataOrThrow } from '../fun/getDataOrThrow'
import { makeQuizQuestions, QuizDifficulty } from '../fun/makeQuizQuestions'
import { setPageTitle } from '../fun/setPageTitle'
import { ICON_CHECKED } from '../icon/ICON_CHECKED'
import { ICON_RIGHT } from '../icon/ICON_RIGHT'
import { ICON_WRONG } from '../icon/ICON_WRONG'
import { _ } from '../model/ccc'
import { DATAURI_LOADING_IMAGE } from '../model/DATAURI_LOADING_IMAGE'
import type { IPerson } from '../model/IPerson'
import { QueryKey } from '../model/QueryKey'
import { quizState } from '../model/quizState'
import { Relationship } from '../model/Relationship'
import { PersonLinkComp } from './PersonLinkComp'
import { PortraitComp, PortraitSize } from './PortraitComp'

export const PageQuizComp = defineComponent<{}>('PageQuizComp', (props, $) => {
	setPageTitle('Kvíz')

	const data = getDataOrThrow()

	const personIdsWithImages = useQuery('personIdsWithImages', () => ({
		key: QueryKey.PersonIdsWithImages,
		load: fetchPersonIdsWithImages,
		params: {},
		staleAfter: Infinity,
	}))

	const images = useQuery('images', () => ({
		key: QueryKey.Images,
		load: async ({ personId }: { personId: string | undefined }) => {
			if (!personId) return []
			return fetchPersonImagesList(personId)
		},
		params: { personId: getQuestion()?.personId },
		isEnabled: !!getQuestion(),
		staleAfter: 1000 * 60 * 30,
	}))

	function getQuestion() {
		return quizState.questions?.at(quizState.questionIndex)
	}

	function getQuestionPerson() {
		const question = getQuestion()
		if (!question) return undefined
		return expectDefined(data.personsById[question.personId])
	}

	return (
		<Show
			it={[
				$when(
					() => quizState.questions == null,
					() => (
						<div
							class={_.ccc_page}
							inert={() => personIdsWithImages.data == null}
						>
							<h1>Kvíz</h1>
							<div class={_.ccc_tiles__centered}>
								<button
									class={_.ccc_button__rounded}
									onclick={() => {
										const questions = makeQuizQuestions(
											data,
											personIdsWithImages.data ?? [],
											QuizDifficulty.ParditkaAlive,
										)
										mutateState($.debugName, 'set questions [tcg7je]', () => {
											quizState.questions = questions
											quizState.questionIndex = 0
											quizState.showCorrect = false
											quizState.difficulty = QuizDifficulty.ParditkaAlive
										})
									}}
								>
									<span>
										<b>Könnyű</b>: csak élő Parditkák
									</span>
								</button>
								<button
									class={_.ccc_button__rounded}
									onclick={() => {
										const questions = makeQuizQuestions(
											data,
											personIdsWithImages.data ?? [],
											QuizDifficulty.ParditkaAndCloseRelativesAlive,
										)
										mutateState($.debugName, 'set questions [tcgwqr]', () => {
											quizState.questions = questions
											quizState.questionIndex = 0
											quizState.showCorrect = false
											quizState.difficulty =
												QuizDifficulty.ParditkaAndCloseRelativesAlive
										})
									}}
								>
									<span>
										<b>Közepes</b>: élő Parditkák és közeli rokonok
									</span>
								</button>
								<button
									class={_.ccc_button__rounded}
									onclick={() => {
										const questions = makeQuizQuestions(
											data,
											personIdsWithImages.data ?? [],
											QuizDifficulty.ParditkaAndCloseRelativesAll,
										)
										mutateState($.debugName, 'set questions [tcgyfu]', () => {
											quizState.questions = questions
											quizState.questionIndex = 0
											quizState.showCorrect = false
											quizState.difficulty =
												QuizDifficulty.ParditkaAndCloseRelativesAll
										})
									}}
								>
									<span>
										<b>Nehéz</b>: élő és elhunyt Parditkák és közeli rokonok
									</span>
								</button>
							</div>
						</div>
					),
				),
				$when(
					() =>
						quizState.questions &&
						quizState.questionIndex < quizState.questions.length,
					() => (
						<>
							<div class={_.ccc_portrait_container}>
								<PortraitComp
									getPerson={() => getQuestionPerson()!}
									getFileName={() =>
										images.data
											? images.data.at(
													getQuestion()!.imageIndex % images.data.length,
												)
											: DATAURI_LOADING_IMAGE
									}
									getSize={() => PortraitSize.Large}
								/>
							</div>
							<div
								class={_.ccc_page}
								inert={() => personIdsWithImages.data == null}
							>
								<h1>Kvíz: ki ő?</h1>
								<div class={_.ccc_tiles__centered}>
									<For
										debugName='Választások'
										each={() => getQuestion()?.choicePersonIds}
										render={({ get }) => (
											<button
												class={() => [
													_.ccc_button__rounded,
													quizState.showCorrect &&
													get() === getQuestion()?.personId
														? _.ccc__selected
														: getQuestion()?.answer === get() && _.ccc__wrong,
												]}
												onclick={() => {
													if (quizState.showCorrect) return
													mutateState(
														$.debugName,
														'set answer, show correct [tcgoen]',
														() => {
															getQuestion()!.answer = get()
															quizState.showCorrect = true
														},
													)
												}}
											>
												<Slot
													isTrustedHtml
													get={() =>
														quizState.showCorrect &&
														get() === getQuestion()?.personId
															? ICON_CHECKED
															: getQuestion()?.answer === get()
																? ICON_WRONG
																: ''
													}
												/>{' '}
												<Slot
													get={() =>
														formatPersonName(
															expectDefined(data.personsById[get()]),
															quizState.difficulty,
														)
													}
												/>
											</button>
										)}
									/>
								</div>
								<p>
									<Slot
										get={() =>
											`${quizState.questionIndex + 1} / ${quizState.questions!.length}`
										}
									/>
								</p>
								<Show
									it={$when(
										() => quizState.showCorrect,
										() => (
											<div class={_.ccc_tiles__centered}>
												<PersonLinkComp get={() => getQuestionPerson()!} />
												<button
													class={_.ccc_button__plus}
													onclick={() =>
														mutateState(
															$.debugName,
															'next question [tcgooj]',
															() => {
																quizState.questionIndex += 1
																quizState.showCorrect = false
															},
														)
													}
												>
													Következő kérdés <Icon svg={ICON_RIGHT} />
												</button>
											</div>
										),
									)}
								/>
							</div>
						</>
					),
				),
			]}
			else={() => (
				<div class={_.ccc_page}>
					<h1>Kvíz: végeredmény</h1>
					<p>
						Eredményed:{' '}
						<Slot
							get={() => {
								const correctCount = quizState.questions!.filter(
									(it) => it.answer === it.personId,
								).length
								const allCount = quizState.questions!.length
								return (
									<>
										{formatNumber(correctCount)} / {formatNumber(allCount)} –{' '}
										<b>
											{formatNumber(
												Math.round((correctCount / allCount) * 100),
											)}
											%
										</b>
									</>
								)
							}}
						/>
					</p>
					<div class={_.ccc_tiles__centered}>
						<button
							class={_.ccc_button__rounded}
							onclick={() => {
								mutateState($.debugName, 'reset quiz [tcguhh]', () => {
									quizState.questions = undefined
								})
							}}
						>
							Újra
						</button>
					</div>
				</div>
			)}
		/>
	)
})

function formatPersonName(person: IPerson, difficulty: QuizDifficulty) {
	if (
		difficulty === QuizDifficulty.ParditkaAlive &&
		person.relationship === Relationship.Parditka
	) {
		return <b>{person.firstName}</b>
	}
	if (person.relationship === Relationship.Parditka) {
		return (
			<span>
				{person.honorific ? <small>{person.honorific}</small> : ''}{' '}
				<span class={_.ccc_parditka}>{person.lastName}</span>{' '}
				<b>{person.firstName}</b>
			</span>
		)
	}
	return (
		<span>
			{person.honorific ? <small>{person.honorific}</small> : ''}{' '}
			{person.lastName ?? ''} <b>{person.firstName}</b>
		</span>
	)
}
